import {
  Intent,
  MenuItem,
  PopoverPosition,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import uniq from 'lodash.uniq';
import * as React from 'react';
import { ItemSelector } from 'vet-bones/components';
import { SelectItem } from 'vet-bones/utils/select';

import { refreshDatabase } from 'src/actions/databases/action-creators/refreshDatabase';
import { TEST_IDS, getDatabaseItemTestId } from 'src/constants/testIds';
import { catalogDatabaseSelector } from 'src/selectors/catalogDatabase';
import { databaseItemsSelector } from 'src/selectors/databaseItems';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isRefreshingDatabasesSelector } from 'src/selectors/isRefreshingDatabases';
import * as copy from 'src/templates/copy';
import { icons } from 'src/templates/icons';
import { colors } from 'src/templates/styles/colors';
import { stopPropagation } from 'src/utils/handlers/stopPropagation';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import {
  INVALID_NAME_MATCHER,
  NAME_STARTS_WITH_MATCHER,
} from 'src/utils/removeInvalidNameChars';
import { stringToSelectItem } from 'src/utils/select/stringToSelectItem';

const createNewDatabaseSelectItemRenderer =
  (catalogDatabase: string, allowDuplicates = false) =>
  (items: SelectItem[], testId?: string) =>
  // eslint-disable-next-line react/display-name
  (
    query: string,
    active: boolean,
    handleClick: React.MouseEventHandler<HTMLElement>
  ) => {
    // eslint-disable-next-line react/destructuring-assignment
    const trimmedQuery = query.trim();

    if (
      !trimmedQuery ||
      (!allowDuplicates &&
        items.some(
          (item) => item.text.toLowerCase() === trimmedQuery.toLowerCase()
        ))
    ) {
      return undefined;
    }

    // validate new database name
    let errorMessage;
    const invalidChars = trimmedQuery.match(INVALID_NAME_MATCHER);

    if (!trimmedQuery.match(NAME_STARTS_WITH_MATCHER)) {
      errorMessage = copy.components.databaseSelector.errorMessages.startsWith;
    } else if (invalidChars) {
      errorMessage = copy.components.databaseSelector.errorMessages.characters(
        uniq(invalidChars)
      );
    } else if (trimmedQuery === catalogDatabase) {
      errorMessage = copy.components.databaseSelector.errorMessages.catalog;
    }

    if (errorMessage) {
      return (
        <MenuItem
          className="sd-menu-item-no-results"
          data-testid={TEST_IDS.database.selectorCreateItemError}
          intent={Intent.DANGER}
          multiline
          text={errorMessage}
        />
      );
    }

    return (
      <MenuItem
        active={active}
        data-testid={testId}
        icon={IconNames.ADD}
        onClick={handleClick}
        shouldDismissPopover={false}
        text={copy.components.defaults.itemSelectorCreate(trimmedQuery)}
      />
    );
  };

export type DatabaseSelectorProps = {
  creatable?: boolean;
  databaseId: string | null;
  disabled?: boolean;
  popoverPosition?: PopoverPosition;
  selectorRef?: React.Ref<ItemSelector<null, SelectItem, SelectItem | null>>;
  setDatabaseId: (id: string | null) => void;
};

export const DatabaseSelector: React.VFC<DatabaseSelectorProps> = ({
  creatable,
  databaseId,
  disabled,
  popoverPosition,
  selectorRef,
  setDatabaseId,
}) => {
  const dispatch = useReduxDispatch();
  const selectedDatabase = React.useMemo(
    () => (databaseId ? stringToSelectItem(databaseId) : null),
    [databaseId]
  );

  const databaseItems = useReduxSelector(databaseItemsSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const isRefreshingDatabases = useReduxSelector(isRefreshingDatabasesSelector);
  const catalogDatabase = useReduxSelector(catalogDatabaseSelector);

  const usableDatabaseItems = React.useMemo(
    () => databaseItems.filter((item) => item.value !== catalogDatabase),
    [databaseItems, catalogDatabase]
  );

  React.useEffect(() => {
    if (!isConnected) {
      setDatabaseId(null);
    }
  }, [isConnected, setDatabaseId]);

  const isSelectorDisabled = !isConnected || isRefreshingDatabases || disabled;

  const handleSelectDatabaseId = (
    item: SelectItem | null,
    evt?: React.MouseEvent
  ) => {
    const nextDatabaseId = item?.value || null;
    stopPropagation(evt);
    setDatabaseId(nextDatabaseId);
    if (nextDatabaseId) {
      dispatch(refreshDatabase(nextDatabaseId));
    }
  };

  return (
    <ItemSelector.Single
      buttonIcon={icons.database}
      buttonIconColor={databaseId ? colors.darkIcon : undefined}
      buttonRightIcon={
        isRefreshingDatabases ? <Spinner size={SpinnerSize.SMALL} /> : undefined
      }
      buttonText={
        databaseId ||
        (creatable
          ? copy.components.databaseSelector.creatablePlaceholder
          : copy.components.databaseSelector.placeholder)
      }
      creatable={creatable}
      createNewItemRenderer={
        creatable
          ? createNewDatabaseSelectItemRenderer(catalogDatabase || 'catalog')
          : undefined
      }
      createNewItemTestId={TEST_IDS.database.selectorCreateItem}
      disabled={isSelectorDisabled}
      filterInputProps={
        {
          'data-testid': TEST_IDS.database.selectorFilter,
          placeholder: creatable
            ? copy.components.defaults.filterOrCreate
            : copy.components.defaults.filter,
        } as any
      }
      items={usableDatabaseItems}
      itemTestId={getDatabaseItemTestId}
      onButtonClick={stopPropagation}
      onClear={stopPropagation}
      onSelect={handleSelectDatabaseId}
      popoverProps={{
        backdropProps: { onClick: stopPropagation },
        position: popoverPosition,
      }}
      resetOnClose
      selected={selectedDatabase}
      selectorRef={selectorRef}
      testId={TEST_IDS.database.selector}
    />
  );
};
